import { FC, FormEvent, Fragment, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';

import ErrorDialog from 'components/organization/dialog/error-dialog';

import RedeliveryDetailAddButton from './inputs/add-detail';
import RedeliveryAddTruckingCompany from './inputs/add-trucking';
import RedeliveryAnyTruckingCompany from './inputs/any-trucking';
import RedeliveryDetailCount from './inputs/count';
import RedeliveryCustomer from './inputs/customer';
import RedeliveryDetailDeleteButton from './inputs/delete-detail';
import RedeliveryDeleteTruckingCompany from './inputs/delete-trucking';
import RedeliveryExpirationDate from './inputs/expiration-date';
import RedeliveryNotes from './inputs/notes';
import RedeliveryDetailReceived from './inputs/received';
import RedeliveryNumber from './inputs/redelivery-number';
import RedeliveryDetailReserved from './inputs/reserved';
import RedeliveryDetailSizeType from './inputs/size-type';
import RedeliveryStartDate from './inputs/start-date';
import { RedeliveryContext } from './redelivery-context';
import useRedeliveryApi from './use-redelivery-api';

const EditRedelivery: FC = () => {
  const navigate = useNavigate();
  const { putRedelivery } = useRedeliveryApi();
  const { getRedeliveryObject, id, location, redeliveryDetails, redeliveryNumber, truckingCompanies } =
    useContext(RedeliveryContext);

  const handleReset = useCallback(() => {
    navigate(
      !location
        ? !id
          ? '/redeliveries'
          : `/redeliveries/${id}`
        : !id
        ? `/redeliveries?location=${location.id}`
        : `/redeliveries/${id}?location=${location.id}`,
      { replace: true }
    );
  }, [id, location, navigate]);

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!id) return;
      putRedelivery(id, getRedeliveryObject());
    },
    [getRedeliveryObject, id, putRedelivery]
  );

  return (
    <form
      className="page"
      data-cy="edit-redelivery-form"
      id="edit-redelivery"
      onReset={handleReset}
      onSubmit={handleSubmit}
    >
      <div className="page-header">
        <h1>REDELIVERY #{redeliveryNumber || 'N/A'}</h1>
        <div className="grid-2">
          <button className="btn-outline" type="reset">
            CANCEL
          </button>
          <button className="btn-primary" type="submit" data-cy="redelivery-submit-button">
            SUBMIT
          </button>
        </div>
      </div>
      <div className="page-content flex-fill">
        <div className="card-primary">
          <div className="card-header">
            <h4>REDELIVERY DETAILS</h4>
          </div>
          <div className="card-body grid-3 auto-rows">
            <RedeliveryNumber />
            <RedeliveryCustomer />
            <RedeliveryNotes className="row-span-2" />
            <RedeliveryStartDate />
            <RedeliveryExpirationDate />
          </div>
        </div>
        <div className="card-primary">
          <div className="card-header">
            <h4>EQUIPMENT DETAILS</h4>
          </div>
          <div className="card-body grid-6">
            {[...redeliveryDetails.keys()].map(id => (
              <Fragment key={id}>
                <RedeliveryDetailSizeType id={id} />
                <RedeliveryDetailCount id={id} />
                <RedeliveryDetailReserved id={id} />
                <RedeliveryDetailReceived id={id} />
                <RedeliveryDetailDeleteButton id={id} />
                <div />
              </Fragment>
            ))}
            <RedeliveryDetailAddButton />
          </div>
        </div>
        <div className="card-primary">
          <div className="card-header">
            <h4>CARRIER DETAILS</h4>
          </div>
          <div className="card-body d-flex g-3 align-items-start">
            <RedeliveryAnyTruckingCompany />
            <RedeliveryAddTruckingCompany />
            <div className="d-flex flex-wrap g-3">
              {[...truckingCompanies.keys()].map(id => (
                <RedeliveryDeleteTruckingCompany key={id} id={id} />
              ))}
            </div>
          </div>
        </div>
      </div>
      <ErrorDialog />
    </form>
  );
};

export default EditRedelivery;
